import { gql, useQuery } from '@apollo/client'
import { BillingSubscriptionStatus } from '__generated__/graphql'
import { useAccessStore } from 'features/Access/infra/react/AccessState'
import { isPast } from 'date-fns'
import dayjs from 'dayjs'
import { PowerIcon, SquareWithArrowIcon, TriangleExclamationIcon } from 'presentation/components/Icons'
import { NoticeButton, NoticeEmphasis } from 'presentation/components/Notice'
import { ResponsiveNotice } from 'presentation/components/ResponsiveNotice/ResponsiveNotice'
import { useNoticeStore } from 'presentation/main/NoticeCarousel'
import { NOTICE_KEYS } from 'presentation/main/NoticeCarousel/Notice.const'
import { CHOOSE_PLAN_PATH, shouldChooseNewPlanForLapsedLegacySubscription } from 'presentation/main/globalModals/FailedInvoiceModal/legacyPastDuePlan'
import { unmaskFailedInvoiceFragment } from 'presentation/main/globalModals/FailedInvoiceModal/unmaskFailedInvoiceFragment'
import { useRestorePlan } from 'presentation/main/globalModals/RestorePlanModal/useRestorePlan'
import { get } from 'presentation/utils/graphql'
import { useCallback, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'

export const SUBSCRIPTION_NOTICE__GET_SUBSCRIPTION = gql(/* GraphQL */ `
  query SubscriptionNotice_GetSubscription {
    myEnterprise {
      id

      name
      subscription {
        ... on BillingSubscriptionActive {
          __typename
          id
          cancelAt
          cancelAtPeriodEnd
          pastDueInvoice {
            id
            createdAt
            items {
              amount
              description
            }
            authorizedAmount
          }
          plan {
            id
            name
            price
            product {
              isLegacy
            }
          }
          upcomingInvoice {
            paymentDate
            totalAmount
          }
        }
        ... on BillingSubscriptionInactive {
          __typename
          id
          status
          plan {
            product {
              isLegacy
            }
          }
        }
      }
    }
  }
`,
)

// The billing webhook clears the stored subscription id when Stripe fully
// deletes the old subscription. These notice/modal hooks must read fresh
// network state so they do not act on a cached past-due subscription after the
// account has already transitioned to "no subscription".
export const SUBSCRIPTION_NOTICE_QUERY_OPTIONS = {
  fetchPolicy: 'network-only' as const,
  nextFetchPolicy: 'cache-first' as const,
}

export const useSubscriptionNotice = () => {
  const access = useAccessStore(state => state.local.access)
  const { data, loading, error } = useQuery<any>(
    SUBSCRIPTION_NOTICE__GET_SUBSCRIPTION,
    SUBSCRIPTION_NOTICE_QUERY_OPTIONS,
  )
  const sub = data?.myEnterprise?.subscription
  const activeSub = get('BillingSubscriptionActive')(sub) ?? undefined
  const inactiveSub = get('BillingSubscriptionInactive')(sub) ?? undefined

  const failedInvoiceSummary = useMemo(() => unmaskFailedInvoiceFragment(activeSub), [activeSub])
  const isActiveLegacyPlan = activeSub?.plan?.product?.isLegacy ?? false
  const isInactiveLegacyPlan = inactiveSub?.plan?.product?.isLegacy ?? false
  const hasCurrentAccessSubscription
    = (access.status === 'loaded' || access.status === 'updating')
    && access.data.status === 'logged-in'
    && !!access.data.subscription
  const {
    restoreWithPayment,
    restoreWithoutPayment,
    loading: isRestorePlanLoading,
  } = useRestorePlan()

  const handleReactivatePlan = useCallback(() => {
    if (failedInvoiceSummary)
      restoreWithPayment(failedInvoiceSummary)
    else
      restoreWithoutPayment()
  }, [failedInvoiceSummary, restoreWithPayment, restoreWithoutPayment])

  const updateNotice = useNoticeStore(store => store.updateNotice)

  useEffect(() => {
    if (loading || error) return
    const wasSetToCancelInPastDate = activeSub?.cancelAt && isPast(activeSub?.cancelAt)
    const shouldChoosePlanForActiveLegacy = shouldChooseNewPlanForLapsedLegacySubscription({
      failedInvoiceSummary,
      hasCancellationPassed: !!wasSetToCancelInPastDate,
      isInactive: false,
      isLegacyPlan: isActiveLegacyPlan,
    })
    const shouldChoosePlanForInactiveLegacy = shouldChooseNewPlanForLapsedLegacySubscription({
      hasCancellationPassed: false,
      isInactive: !!inactiveSub,
      isLegacyPlan: isInactiveLegacyPlan,
    })

    const isTrulyInactive = inactiveSub?.status
      && ![
        BillingSubscriptionStatus.Active,
        BillingSubscriptionStatus.Trialing,
      ].includes(inactiveSub.status)
    const hasInactiveSubWithoutStatus = !!inactiveSub && !inactiveSub.status
    const hasNoSub = !hasCurrentAccessSubscription || (!activeSub && !inactiveSub)

    /** CASE: no current subscription access */
    if (hasNoSub || shouldChoosePlanForInactiveLegacy || hasInactiveSubWithoutStatus) {
      updateNotice(
        NOTICE_KEYS.SUBSCRIPTION,
        (
          <ResponsiveNotice
            colorScheme='error'
            icon={TriangleExclamationIcon}
            textContent={(
              <>
                <NoticeEmphasis>
                  Your subscription for
                  {' '}
                  {data?.myEnterprise?.name}
                  {' '}
                  has been cancelled.
                </NoticeEmphasis>
                {' '}
                {hasCurrentAccessSubscription && shouldChoosePlanForInactiveLegacy
                  ? 'Your previous plan is no longer available. Please subscribe to a current plan to continue using Propelio.'
                  : 'Please subscribe to a plan to continue using Propelio.'}
              </>
            )}
            actionsContent={(
              <>
                <NoticeButton
                  variant='solid'
                  colorScheme='highlight'
                  leftIcon={<SquareWithArrowIcon fontSize='2' />}
                  as={Link}
                  to={CHOOSE_PLAN_PATH}
                >
                  Choose Plan
                </NoticeButton>
              </>
            )}
          />
        ),
      )

    /** CASE: subscription was set to cancel in past date */
    } else if (wasSetToCancelInPastDate) {
      updateNotice(
        NOTICE_KEYS.SUBSCRIPTION,
        (
          <ResponsiveNotice
            colorScheme='error'
            icon={TriangleExclamationIcon}
            textContent={(
              <>
                <NoticeEmphasis>
                  Your subscription for
                  {' '}
                  {data?.myEnterprise?.name}
                  {' '}
                  has been cancelled.
                </NoticeEmphasis>
                {' '}
                {shouldChoosePlanForActiveLegacy
                  ? (
                    <>
                      {' '}
                      Your previous plan is no longer available. Choose a current plan to continue using Propelio.
                    </>
                  )
                  : activeSub.cancelAt && (
                    <>
                      {' '}
                      Your team will lose access to Propelio on
                      {' '}
                      {dayjs(activeSub.cancelAt).format('MMM D, YYYY')}
                      {' '}
                      unless you reactivate your plan.
                    </>
                  )}
              </>
            )}
            actionsContent={(
              <>
                {shouldChoosePlanForActiveLegacy
                  ? (
                    <NoticeButton
                      variant='solid'
                      colorScheme='highlight'
                      leftIcon={<SquareWithArrowIcon fontSize='2' />}
                      as={Link}
                      to={CHOOSE_PLAN_PATH}
                    >
                      Choose Plan
                    </NoticeButton>
                  )
                  : (
                    <NoticeButton
                      variant='solid'
                      colorScheme='highlight'
                      leftIcon={<PowerIcon boxSize='2' />}
                      onClick={handleReactivatePlan}
                      isLoading={isRestorePlanLoading}
                    >
                      REACTIVATE PLAN
                    </NoticeButton>
                  )}
              </>
            )}
          />
        ),
      )
    } else if (isTrulyInactive && inactiveSub && !isInactiveLegacyPlan) {
      updateNotice(
        NOTICE_KEYS.SUBSCRIPTION,
        (
          <ResponsiveNotice
            colorScheme='error'
            icon={TriangleExclamationIcon}
            textContent={(
              <>
                <NoticeEmphasis>
                  Your subscription for
                  {' '}
                  {data?.myEnterprise?.name}
                  {' '}
                  has been cancelled.
                </NoticeEmphasis>
                {' '}
                Reactivate your previous plan to continue using Propelio.
              </>
            )}
            actionsContent={(
              <>
                <NoticeButton
                  variant='solid'
                  colorScheme='highlight'
                  leftIcon={<PowerIcon boxSize='2' />}
                  onClick={handleReactivatePlan}
                  isLoading={isRestorePlanLoading}
                >
                  REACTIVATE PLAN
                </NoticeButton>
              </>
            )}
          />
        ),
      )
    }

    return () => updateNotice(NOTICE_KEYS.SUBSCRIPTION, null)
  }, [
    loading,
    error,
    activeSub?.cancelAtPeriodEnd,
    hasCurrentAccessSubscription,
    data?.myEnterprise?.name,
    activeSub?.cancelAt,
    failedInvoiceSummary,
    handleReactivatePlan,
    inactiveSub,
    isActiveLegacyPlan,
    isInactiveLegacyPlan,
    isRestorePlanLoading,
  ])

  return null
}
